import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, RotateCcw, Wand2 } from "lucide-react";
import { deleteSunoVoice, remakeSunoVoice } from "@/lib/sunoVoicesApi";
import VoiceHealthLine from "@/components/sunovoice/VoiceHealthLine";
import VoiceCompare from "@/components/sunovoice/VoiceCompare";
import { safeApiError } from "@/lib/safeErrorText";

function Waiting({ testId, title, body }) {
  return (
    <section data-testid={testId} className="flex items-start gap-4 rounded-3xl border border-amber-300/25 bg-amber-300/[0.06] p-6">
      <Loader2 className="mt-1 h-6 w-6 shrink-0 animate-spin text-amber-200" />
      <div><p className="font-display text-lg font-semibold">{title}</p><p className="mt-1 text-[13px] text-white/60">{body}</p></div>
    </section>
  );
}

function Ready({ voice, onChanged }) {
  return (
    <div className="space-y-8">
      <section data-testid="voice-ready" className="rounded-3xl border border-emerald-400/30 bg-emerald-400/[0.07] p-6">
        <p className="flex items-center gap-2 font-display text-lg font-semibold text-emerald-100"><CheckCircle2 className="h-5 w-5" /> “{voice.name}” is ready</p>
        <p className="mt-1 text-[13px] text-white/60">
          ELPM now sings in your voice. {voice.is_default ? "Every new song starts with it picked" : "Pick it from the Model menu on Generate Song"} — or start a song with it now.
        </p>
        <Link to={`/create/song?voice=${voice.id}`} data-testid="voice-make-song" className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-5 py-2.5 font-display text-sm font-semibold transition-transform duration-200 hover:scale-[1.02]">
          <Wand2 className="h-4 w-4" /> Make a song with this voice
        </Link>
        <VoiceHealthLine voice={voice} onChanged={onChanged} />
      </section>
      <VoiceCompare voice={voice} onChanged={onChanged} />
    </div>
  );
}

function Failed({ voice }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const restart = async () => {
    setBusy(true);
    try { await deleteSunoVoice(voice.id); navigate("/create/my-voices?new=1"); }
    catch (e) { toast.error("Couldn't start over", { description: safeApiError(e) }); setBusy(false); }
  };
  return (
    <section data-testid="voice-error" className="rounded-3xl border border-rose-400/30 bg-rose-500/10 p-6">
      <p className="flex items-start gap-2 text-[14px] text-rose-100"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{voice.error || "ELPM couldn't use that recording."}</p>
      <p className="mt-2 text-[12px] text-white/55">ELPM couldn't learn from that stretch. Start again with a cleaner one — just your voice, no music, close to the mic.</p>
      <button type="button" data-testid="voice-error-restart" onClick={restart} disabled={busy} className="mt-4 inline-flex items-center gap-2 rounded-full bg-cyan-300 px-5 py-2.5 text-[13px] font-semibold text-black disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Start again with a new recording
      </button>
    </section>
  );
}

function Broken({ voice, onChanged }) {
  const [busy, setBusy] = useState(false);
  const remake = async () => {
    setBusy(true);
    try { onChanged(await remakeSunoVoice(voice.id)); toast.success("Re-making your voice", { description: "ELPM is writing a short phrase for you to sing again." }); }
    catch (e) { toast.error("Couldn't start the re-make", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="voice-broken" className="rounded-3xl border border-amber-300/35 bg-amber-400/[0.08] p-6">
      <p className="flex items-start gap-2 font-display text-lg font-semibold text-amber-100"><AlertTriangle className="mt-1 h-5 w-5 shrink-0" /> “{voice.name}” needs to be re-made</p>
      <p className="mt-2 text-[13px] leading-relaxed text-white/65">The music engine can't sing with this voice any more. ELPM re-makes it from the same recording — you just sing the short check phrase again (about 2 minutes). {voice.is_default ? "It stays your default voice." : ""}</p>
      <button type="button" data-testid="voice-remake" onClick={remake} disabled={busy} className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-300 px-5 py-2.5 text-[13px] font-semibold text-black transition-transform duration-200 hover:scale-[1.02] disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Re-make voice
      </button>
    </section>
  );
}

/** Everything except the phrase step: waiting for the phrase, building, ready, needs re-making, failed. */
export default function VoiceStatePanel({ voice, onChanged }) {
  if (voice.status === "phrase") return <Waiting testId="voice-waiting-phrase" title="ELPM is listening to your singing…" body="It's writing a short phrase for you to sing back — usually under a minute. This page updates on its own." />;
  if (voice.status === "creating") return <Waiting testId="voice-building" title="ELPM is building your voice…" body="This can take a few minutes. You can leave this page — it keeps going and shows Ready here and in My Voices." />;
  if (voice.status === "ready") return <Ready voice={voice} onChanged={onChanged} />;
  if (voice.status === "broken") return <Broken voice={voice} onChanged={onChanged} />;
  return <Failed voice={voice} />;
}
